import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { clsx } from 'clsx';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
import { useIsClient } from '@crehana/react-hooks';
import { TextField, TextLink } from '@crehana/ui';
import { getNextUrl, getQueryParam, isInIframe } from '@crehana/utils';
import { useLoginUserMutation } from '@crehana/web';

import { validations } from '@/shared/constants';
import {
  allowFloatingShoppingCartReminderVisibility,
  redirectWithQueryParams,
  setRecentlyLoggedStatus,
} from '@/shared/utils';

import { AvailableFeatureFlags } from '../../types';
import EmailInput from '../EmailInput/';
import B2BLoginButton from './B2BLoginButton';
import B2CLoginSocialButtons from './B2CLoginSocialButtons';
import Divider from './Divider';

type LoginFormProps = {
  featureFlags: AvailableFeatureFlags;
};

const LoginForm: FC<React.PropsWithChildren<LoginFormProps>> = ({
  featureFlags: { hideFacebookButton, hideFacebookLink },
}) => {
  const { t, countryCode } = useTranslation();
  const [submitError, setSubmitError] = useState<string | undefined | null>();
  const defaultEmail = getQueryParam('email_user') || '';
  const isClient = useIsClient();
  const [loginUser] = useLoginUserMutation();
  const nextUrl = getNextUrl();
  const { logEvent } = useAmplitude();
  const sourcePage =
    (getQueryParam('source_page') as string)?.toLowerCase() || null;
  const isRenovationFlow = sourcePage === 'renovacion';
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      username: defaultEmail as string,
      password: '',
    },
  });

  const getErrorMessage = (message: string): string => {
    if (
      message === 'Usuario y/o contraseña incorrectos' &&
      countryCode === 'br'
    )
      return 'Usuário e/ou senha incorreta';

    return message;
  };

  const onSubmit = handleSubmit(async data => {
    const res = await loginUser({
      variables: {
        ...data,
        nextUrl,
        giftcardToken:
          (getQueryParam('giftcard_token') as string | undefined) || null,
      },
    });

    if (res?.data?.loginUser) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const { messages, success, redirect } = res?.data?.loginUser;

      if (success) {
        logEvent({
          event: 'Successful SignIn',
          data: {
            source: 'Email',
          },
        });
        if (!isInIframe()) {
          setRecentlyLoggedStatus();
          allowFloatingShoppingCartReminderVisibility();
          window.location.replace(redirect || '');
        } else {
          setRecentlyLoggedStatus();
          allowFloatingShoppingCartReminderVisibility();
          window.location.replace(redirect || '');
        }
      } else {
        setSubmitError(
          getErrorMessage((messages && messages[0]?.message) || ''),
        );
      }
    }
  });

  useEffect(() => {
    logEvent({
      event: 'Render Signin',
      data: {
        ...getSourceEventParams(),
        Type: 'URL',
      },
    });
  }, []);

  const toggleView = () => {
    redirectWithQueryParams('/registro/');
  };

  return (
    // @ts-expect-error
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="text-center">
        <h3 className="font-h3">
          {t(isRenovationFlow ? 'FORM_TITLE_RENOVATION' : 'FORM_TITLE')}
        </h3>
        <p className="font-body3 mb-24">
          {t(
            isRenovationFlow
              ? 'FORM_DESCRIPTION_RENOVATION'
              : 'FORM_DESCRIPTION',
          )}
        </p>
      </div>
      {isClient && (
        <B2CLoginSocialButtons
          nextUrl={nextUrl}
          hideFacebookLink={hideFacebookLink}
          hideFacebookButton={hideFacebookButton}
        />
      )}
      {/* Username */}
      <Controller
        control={control}
        name="username"
        rules={{
          required: validations.required(t),
          pattern: validations.email(t),
        }}
        render={({ ref, onBlur, onChange, value }) => (
          <EmailInput
            id="username"
            value={value}
            onChange={event => {
              onChange(event);
              if (submitError) {
                setSubmitError('');
              }
            }}
            ref={ref}
            onBlur={onBlur}
            className="mb-24 mt-24"
            label={t('INPUT_EMAIL_LABEL')}
            hint={t('INPUT_EMAIL_HINT')}
            defaultValue={defaultEmail as string | undefined}
            disabled={isSubmitting}
            errorText={errors.username?.message}
            full
            autoFocus
            transparencyMode
            isLoaderWhite
            hideB2BLoginMsg
          />
        )}
      />
      {/* Password */}
      <Controller
        control={control}
        name="password"
        rules={{
          required: validations.required(t),
          minLength: validations.minLength(t),
          maxLength: validations.maxLength(t),
        }}
        render={({ onBlur, onChange, value }) => (
          <TextField
            className="mb-8"
            value={value}
            id="password"
            type="password"
            label={t('INPUT_PASSWORD_LABEL')}
            hint={t('INPUT_PASSWORD_HINT')}
            onBlur={onBlur}
            onChange={event => {
              onChange(event);
              if (submitError) {
                setSubmitError('');
              }
            }}
            disabled={isSubmitting}
            errorText={errors.password?.message}
            full
            transparencyMode
          />
        )}
      />
      <a
        href="/entrar/olvidaste-contrasena/"
        className="block font-body4 tw-font-semibold text-secondary-main mb-24"
      >
        {t('FORGOT_PASSWORD')}
      </a>
      <button
        type="submit"
        className={clsx(
          'tw-mb-2 tw-mx-auto tw-btn-primary-s tw-w-full tw-flex tw-justify-center',
          {
            'tw-btn-disabled': isSubmitting,
          },
        )}
      >
        {t(isSubmitting ? 'SENDING' : 'BUTTON_SUBMIT_LABEL')}
      </button>
      {submitError && (
        <div className="mb-16">
          <span
            className="block mt-4 tw-font-semibold font-legals text-red-main"
            role="alert"
          >
            {submitError}
          </span>
        </div>
      )}
      <div className="flex flex-col md:flex-row items-center justify-between mt-24">
        <span className="font-body3 mb-12 md:m-0">
          {t('FIRST_TIME_IN_CREHANA')}
        </span>
        <TextLink label={t('REGISTER')} onClick={toggleView} />
      </div>
      <Divider className="py-20" lineClassName="bg-base-lighter-16 h-2" />
      <B2BLoginButton />
    </form>
  );
};

export default LoginForm;
